import { fetchJSON } from '@eventbrite/http';
import { pruneObject } from '@eventbrite/redux-destination';
import {
    deepKeysToSnake,
    keysCamelToSnake,
} from '@eventbrite/transformation-utils';
import { $FixMe } from '@eventbrite/ts-utils';
import Cookies from 'cookies-js';
import omit from 'lodash/omit';
import moment from 'moment-timezone';
import { formatUrl } from 'url-lib';
import { CURRENT_FUTURE, QUALITY } from '../../constants';
import { bboxShape } from '../../types/index';

const searchUrl = '/api/v3/destination/search/';
const searchAutocompleteSuggestionsUrl =
    '/api/v3/destination/search/autocomplete/';

const shouldShowDebugInfo = (
    isStaff: boolean,
    history?: { location?: { hash?: string } },
    searchQueryFiltered?: Pick<
        {
            bbox?: bboxShape;
            places?: string[];
            clientTimezone?: any;
        },
        'bbox' | 'clientTimezone'
    > & { hash?: string },
) => {
    // Backend does not support debug info when deduping is used
    // https://github.com/eventbrite/search_service//blob/f272067cfc357b14742262b135379cc035985a8c/search_service/common/elasticsearch/builders/execute.py#L168
    if (searchQueryFiltered?.hash) {
        return false;
    }

    if (!history) {
        return false;
    }

    return isStaff && history.location?.hash === '#debug';
};

// Check if the client_timezone of the query is correct. If we
// received it from the server, it may not be correct because
// it was guessed with GeoIP rather than browser-side JS.
// There may not be any client_timezone at all, this is currently only
// sent for online searches.
const CLIENT_TIMEZONE_COOKIE_NAME = 'client_timezone';
const repairClientTimezone = (searchQuery: { clientTimezone?: any }) => {
    if (searchQuery.clientTimezone) {
        const momentGuessedTimezone = moment.tz.guess();

        if (
            momentGuessedTimezone &&
            searchQuery.clientTimezone !== momentGuessedTimezone
        ) {
            Cookies.expire(CLIENT_TIMEZONE_COOKIE_NAME);
            Cookies.set(CLIENT_TIMEZONE_COOKIE_NAME, momentGuessedTimezone);

            return {
                ...searchQuery,
                clientTimezone: momentGuessedTimezone,
            };
        }
    }

    return searchQuery;
};

interface SearchRequestParameters {
    event_search: $FixMe;
    'expand.destination_event': string[];
    debug?: boolean;
    debug_experiment_overrides?: $FixMe;
}

export const search = ({
    searchQuery,
    experimentOverride,
    isStaff,
    history,
}: {
    searchQuery: {
        bbox?: bboxShape;
        places?: string[];
        clientTimezone?: any;
        dates?: string[];
        durationRange?: { from: number; to: number };
        price?: string;
        tags?: string[];
        onlineEventsOnly?: boolean;
    };
    experimentOverride?: $FixMe;
    isStaff?: boolean;
    history?: { location?: { hash?: string } };
    continuation?: string;
}) => {
    // The search API allows either a bbox or a WOF placeID but not both
    let searchQueryFiltered =
        searchQuery.bbox && searchQuery.places
            ? omit(searchQuery, 'places')
            : searchQuery;

    searchQueryFiltered = repairClientTimezone(searchQueryFiltered);

    let requestParameters: SearchRequestParameters = {
        event_search: deepKeysToSnake(
            pruneObject({
                ...searchQueryFiltered,
                dedup: true,
                page_size: 20,
            }),
        ),
        'expand.destination_event': [
            'primary_venue',
            'image',
            'ticket_availability',
            'saves',
            'event_sales_status',
            'primary_organizer',
            'public_collections',
        ],
    };

    if (
        shouldShowDebugInfo(
            isStaff || false,
            history || {},
            searchQueryFiltered,
        )
    ) {
        requestParameters = {
            ...requestParameters,
            debug_experiment_overrides: {
                [experimentOverride.experimentName]: experimentOverride.variant,
            },
        };
    }

    if (
        experimentOverride &&
        experimentOverride.experimentName &&
        experimentOverride.experimentName !== 'none'
    ) {
        requestParameters = {
            ...requestParameters,
        };
    }

    const fetchReq = fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify(requestParameters),
    });

    return fetchReq;
};

interface SearchAutoCompleteParameters {
    placeId?: string;
    onlineEventsOnly?: boolean;
    query?: string;
}

export const searchAutocompleteSuggestions = ({
    placeId,
    onlineEventsOnly,
    query,
}: SearchAutoCompleteParameters) => {
    let queryParams: $FixMe = {
        placeId,
        q: query,
        completionTypes: ['event', 'query'],
        'expand.destination_event': ['primary_venue', 'image'],
    };

    if (onlineEventsOnly) {
        queryParams.onlineEventsOnly = true;
    }

    queryParams = keysCamelToSnake(queryParams);
    return fetchJSON(formatUrl(searchAutocompleteSuggestionsUrl, queryParams));
};

export const searchEventSuggestions = ({
    placeId,
    onlineEventsOnly,
}: {
    placeId: string;
    onlineEventsOnly: boolean;
}) =>
    fetchJSON(searchUrl, {
        method: 'POST',
        body: JSON.stringify({
            event_search: {
                places: placeId ? [placeId] : [],
                online_events_only: onlineEventsOnly,
                dates: CURRENT_FUTURE,
                sort: QUALITY,
                aggs: {
                    organizertagsautocomplete_agg: { size: 50 },
                    tags: {},
                    dates: {},
                },
            },
            'expand.destination_event': [
                'primary_venue',
                'image',
                'ticket_availability',
                'saves',
            ],
        }),
    });
